import { AlertTriangle, Info, X } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { ReactNode } from "react";

interface ErrorAlertProps {
  message: string;
  title?: string;
  onDismiss?: () => void;
  className?: string;
  testId?: string;
}

export function ErrorAlert({
  message,
  title,
  onDismiss,
  className = "",
  testId,
}: ErrorAlertProps) {
  const { t } = useTranslation();

  return (
    <div
      className={`mt-3 rounded bg-red-50 p-4 ${className}`}
      data-testid={testId}
    >
      <div className="flex">
        <div className="shrink-0">
          <AlertTriangle className="h-5 w-5 text-red-400" aria-hidden="true" />
        </div>
        <div className="ml-3">
          {title && (
            <h3 className="text-sm font-medium text-red-800">{title}</h3>
          )}
          <div className={`${title ? "mt-2" : ""} text-sm text-red-700`}>
            <p>{message}</p>
          </div>
        </div>
        {onDismiss && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                onClick={onDismiss}
                className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100 focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50 focus:outline-hidden"
                aria-label={t("dismiss_error")}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface WarningAlertProps {
  message: string | ReactNode;
  title?: string;
  onDismiss?: () => void;
  className?: string;
}

export function WarningAlert({
  message,
  title,
  onDismiss,
  className = "",
}: WarningAlertProps) {
  const { t } = useTranslation();

  return (
    <div className={`rounded bg-yellow-50 p-4 ${className}`}>
      <div className="flex">
        <div className="shrink-0">
          <AlertTriangle
            className="h-5 w-5 text-yellow-400"
            aria-hidden="true"
          />
        </div>
        <div className="ml-3">
          {title && (
            <h3 className="text-sm font-medium text-yellow-800">{title}</h3>
          )}
          <div className={`${title ? "mt-2" : ""} text-sm text-yellow-700`}>
            {typeof message === "string" ? <p>{message}</p> : message}
          </div>
        </div>
        {onDismiss && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                onClick={onDismiss}
                className="inline-flex rounded-md bg-yellow-50 p-1.5 text-yellow-500 hover:bg-yellow-100 focus:ring-2 focus:ring-yellow-600 focus:ring-offset-2 focus:ring-offset-yellow-50 focus:outline-hidden"
                aria-label={t("dismiss_warning")}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface InlineErrorAlertProps {
  message: string;
  className?: string;
}

export function InlineErrorAlert({
  message,
  className = "",
}: InlineErrorAlertProps) {
  return (
    <div className={`mt-2 flex items-center text-sm text-red-600 ${className}`}>
      <AlertTriangle className="mr-1.5 h-4 w-4 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

interface MutedAlertProps {
  message: string | ReactNode;
  title?: string;
  onDismiss?: () => void;
  className?: string;
}

export function MutedAlert({
  message,
  title,
  onDismiss,
  className = "",
}: MutedAlertProps) {
  const { t } = useTranslation();

  return (
    <div className={`rounded bg-gray-100 p-4 ${className}`}>
      <div className="flex">
        <div className="shrink-0">
          <Info className="h-5 w-5 text-gray-500" aria-hidden="true" />
        </div>
        <div className="ml-3">
          {title && (
            <h3 className="text-sm font-medium text-gray-800">{title}</h3>
          )}
          <div className={`${title ? "mt-2" : ""} text-sm text-gray-700`}>
            {typeof message === "string" ? <p>{message}</p> : message}
          </div>
        </div>
        {onDismiss && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                onClick={onDismiss}
                className="inline-flex rounded-md bg-gray-100 p-1.5 text-gray-500 hover:bg-gray-200 focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 focus:ring-offset-gray-100 focus:outline-hidden"
                aria-label={t("dismiss_info")}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface InfoAlertProps {
  children: ReactNode;
  color?: string;
  icon?: ReactNode;
  className?: string;
}

const DEFAULT_INFO_COLOR = "#3B82F6";

export function InfoAlert({
  children,
  color = DEFAULT_INFO_COLOR,
  icon,
  className = "",
}: InfoAlertProps) {
  const backgroundColor = `${color}20`;

  return (
    <div style={{ backgroundColor }} className={`rounded p-4 ${className}`}>
      <div className="flex items-center">
        {icon && <div className="mr-3 shrink-0">{icon}</div>}
        <div style={{ color }}>{children}</div>
      </div>
    </div>
  );
}
